import { type GameState, type Unit } from "@dailywars/core/engine";

import type { CanvasBoardAnimation } from "#/game/animation/types";
import { getExplosionFrame } from "#/game/fxSprites";

import { getUnitAttackFlash, getUnitDeathDrawEffect } from "./boardAnimations";
import {
  ATTACKABLE_TILE_BORDER_INSET,
  ATTACKABLE_TILE_BORDER_WIDTH,
  ATTACKABLE_TILE_FILL,
  ATTACKABLE_TILE_STROKE,
  SELECTED_UNIT_BORDER_WIDTH,
  SELECTED_UNIT_STROKE,
  TILE_SIZE,
  UNMOVED_TILE_FILL,
  UNMOVED_TILE_STROKE,
  type CanvasViewState,
} from "./boardConstants";
import { drawSpriteUnit } from "./boardSpriteUnitDraw";
import { drawUnitBadges } from "./boardUnitBadges";
import { resolveUnitDrawLayout, type UnitDrawLayout } from "./boardUnitGeometry";
import { unmovedHighlightOpacity } from "./unmovedHighlight";

function drawUnitTileFill({
  ctx,
  unit,
  fill,
  opacity,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  fill: string;
  opacity: number;
}): void {
  if (opacity <= 0) return;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = fill;
  ctx.fillRect(unit.pos.x * TILE_SIZE, unit.pos.y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
  ctx.restore();
}

function drawUnitBorder({
  ctx,
  layout,
  stroke,
  lineWidth,
  opacity,
}: {
  ctx: CanvasRenderingContext2D;
  layout: UnitDrawLayout;
  stroke: string;
  lineWidth: number;
  opacity: number;
}): void {
  if (opacity <= 0) return;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.strokeRect(
    layout.drawX + ATTACKABLE_TILE_BORDER_INSET,
    layout.drawY + ATTACKABLE_TILE_BORDER_INSET,
    TILE_SIZE - ATTACKABLE_TILE_BORDER_INSET * 2,
    TILE_SIZE - ATTACKABLE_TILE_BORDER_INSET * 2,
  );
  ctx.restore();
}

function drawUnitHighlights({
  ctx,
  unit,
  layout,
  viewState,
  attackable,
  unmoved,
  unmovedOpacity,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
  viewState: CanvasViewState;
  attackable: Set<string>;
  unmoved: Set<string>;
  unmovedOpacity: number;
}): void {
  if (attackable.has(unit.id)) {
    drawUnitTileFill({ ctx, unit, fill: ATTACKABLE_TILE_FILL, opacity: 1 });
  } else if (unmoved.has(unit.id)) {
    drawUnitTileFill({ ctx, unit, fill: UNMOVED_TILE_FILL, opacity: unmovedOpacity });
  }

  if (viewState.selectedUnitId === unit.id) {
    drawUnitBorder({
      ctx,
      layout,
      stroke: SELECTED_UNIT_STROKE,
      lineWidth: SELECTED_UNIT_BORDER_WIDTH,
      opacity: 1,
    });
  } else if (attackable.has(unit.id)) {
    drawUnitBorder({
      ctx,
      layout,
      stroke: ATTACKABLE_TILE_STROKE,
      lineWidth: ATTACKABLE_TILE_BORDER_WIDTH,
      opacity: 1,
    });
  } else if (unmoved.has(unit.id)) {
    drawUnitBorder({
      ctx,
      layout,
      stroke: UNMOVED_TILE_STROKE,
      lineWidth: ATTACKABLE_TILE_BORDER_WIDTH,
      opacity: unmovedOpacity,
    });
  }
}

function drawDeathExplosion({
  ctx,
  layout,
  deathEffect,
}: {
  ctx: CanvasRenderingContext2D;
  layout: UnitDrawLayout;
  deathEffect: NonNullable<ReturnType<typeof getUnitDeathDrawEffect>>;
}): void {
  if (!deathEffect.explosionKind) return;
  const frame = getExplosionFrame({
    kind: deathEffect.explosionKind,
    frame: deathEffect.explosionFrame,
  });
  if (!frame) return;
  const drawW = Math.min(frame.sw, TILE_SIZE);
  const drawH = Math.min(frame.sh, TILE_SIZE);
  const drawX = layout.drawX + (TILE_SIZE - drawW) / 2;
  const drawY = layout.drawY + (TILE_SIZE - drawH) / 2;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(frame.img, frame.sx, frame.sy, frame.sw, frame.sh, drawX, drawY, drawW, drawH);
}

function drawSingleUnit({
  ctx,
  unit,
  state,
  viewState,
  animation,
  nowMs,
  attackable,
  unmoved,
  unmovedOpacity,
  isGhost,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  state: GameState;
  viewState: CanvasViewState;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
  attackable: Set<string>;
  unmoved: Set<string>;
  unmovedOpacity: number;
  isGhost: boolean;
}): void {
  const layout = resolveUnitDrawLayout({ unit, viewState, animation, nowMs });
  const deathEffect = getUnitDeathDrawEffect({ unit, animation, nowMs, isGhost });
  const hitFlash = getUnitAttackFlash({ pos: layout.pos, animation, nowMs });
  drawUnitHighlights({
    ctx,
    unit,
    layout,
    viewState,
    attackable,
    unmoved,
    unmovedOpacity,
  });

  if (deathEffect && !deathEffect.showUnit) {
    drawDeathExplosion({ ctx, layout, deathEffect });
    return;
  }

  ctx.save();
  drawSpriteUnit({ ctx, unit, state, layout, hitFlash, animation, nowMs });
  ctx.restore();
  drawUnitBadges({ ctx, unit, layout, animation });
}

function animationGhostExtraUnits({
  state,
  animation,
}: {
  state: GameState;
  animation: CanvasBoardAnimation | null;
}): Unit[] {
  const ghosts =
    animation?.type === "move" || animation?.type === "attackFx"
      ? (animation.ghostUnits ?? [])
      : [];
  return ghosts.filter((ghost) => !state.units.some((u) => u.id === ghost.id));
}

export function drawUnitsLayer({
  ctx,
  state,
  viewState,
  animation,
  nowMs,
}: {
  ctx: CanvasRenderingContext2D;
  state: GameState;
  viewState: CanvasViewState;
  animation: CanvasBoardAnimation | null;
  nowMs: number;
}): void {
  const attackable = new Set(viewState.attackableUnitIds);
  const unmoved = new Set(viewState.unmovedHighlightUnitIds);
  const unmovedOpacity = unmovedHighlightOpacity({
    fadeStartedAtMs: viewState.unmovedHighlightFadeStartedAtMs,
    nowMs,
  });
  const units = [...state.units, ...animationGhostExtraUnits({ state, animation })];
  for (const unit of units) {
    drawSingleUnit({
      ctx,
      unit,
      state,
      viewState,
      animation,
      nowMs,
      attackable,
      unmoved,
      unmovedOpacity,
      isGhost: !state.units.some((u) => u.id === unit.id),
    });
  }
}
